/**
 * i18n bootstrap — English + Urdu with RTL wiring.
 *
 * On every language change we update <html lang> and <html dir> so that Urdu
 * (`ur`) flips the whole document to RTL and swaps the font (see tokens.css).
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import ur from "./ur.json";

export const SUPPORTED_LANGUAGES = ["en", "ur"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Languages that must render right-to-left. */
export const RTL_LANGUAGES: readonly Language[] = ["ur"];

export function isRtl(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang as Language);
}

/** Apply <html lang> + <html dir> for the given language. */
export function applyDocumentDirection(lang: string): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("lang", lang);
  root.setAttribute("dir", isRtl(lang) ? "rtl" : "ltr");
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ur: { translation: ur },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "boond.lang",
    },
  });

// Keep document direction in sync with the active language.
applyDocumentDirection(i18n.resolvedLanguage ?? "en");
i18n.on("languageChanged", applyDocumentDirection);

export default i18n;
